import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { Announcement } from '../../services/communicationsService'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface AnnouncementFormProps {
  announcement?: Announcement
  churchId: string
  onSubmit: (data: Partial<Announcement>) => Promise<void>
  onCancel: () => void
}

const AnnouncementForm: React.FC<AnnouncementFormProps> = ({
  announcement,
  churchId,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState({
    title: announcement?.title || '',
    content: announcement?.content || '',
    priority: announcement?.priority || 'medium' as const,
    start_date: announcement?.start_date || new Date().toISOString().split('T')[0],
    end_date: announcement?.end_date || '',
    is_pinned: announcement?.is_pinned || false,
    target_roles: announcement?.target_roles || [],
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await onSubmit({
        ...formData,
        church_id: churchId,
        target_roles: formData.target_roles.length > 0 ? formData.target_roles : undefined,
      })
    } catch (err: any) {
      setError(err.message || 'Failed to save announcement')
    } finally {
      setLoading(false)
    }
  }

  const toggleRole = (role: string) => {
    setFormData(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }))
  }

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <DialogHeader>
          <DialogTitle className="text-gray-900 dark:text-gray-100">
            {announcement ? 'Edit Announcement' : 'New Announcement'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
            />
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label htmlFor="content">Content *</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              rows={6}
              required
            />
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label>Priority</Label>
            <div className="flex gap-2">
              {(['low', 'medium', 'high', 'urgent'] as const).map(priority => (
                <Button
                  key={priority}
                  type="button"
                  variant={formData.priority === priority ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, priority }))}
                  className="capitalize"
                >
                  {priority}
                </Button>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date *</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">End Date (Optional)</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
              />
            </div>
          </div>

          {/* Target Roles */}
          <div className="space-y-2">
            <Label>Target Audience (Leave empty for all)</Label>
            <div className="flex flex-wrap gap-2">
              {['pastor', 'elder', 'deacon', 'member'].map(role => (
                <Button
                  key={role}
                  type="button"
                  variant={formData.target_roles.includes(role) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleRole(role)}
                  className="capitalize"
                >
                  {role}
                </Button>
              ))}
            </div>
          </div>

          {/* Pin Announcement */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_pinned"
              checked={formData.is_pinned}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_pinned: checked as boolean }))}
            />
            <Label htmlFor="is_pinned" className="text-sm font-normal cursor-pointer">
              Pin this announcement (appears at top)
            </Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Saving...' : announcement ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AnnouncementForm
